/**
 * Canonical per-check export identity — the single source of truth for the
 * XLSX export filename. Imported by both the PDF/server side (embedded
 * attachment, on-disk write, route Content-Disposition) and the dashboard
 * (download attribute) so every surface emits a byte-identical filename
 * (Rule 12 coherence).
 *
 * Isomorphic by design: string operations only, NO Node-only imports — safe
 * to import from browser-bundled dashboard components.
 *
 * @module shared/checkExport
 */

/**
 * Slugify a check_name to a filename-safe kebab fragment.
 * Lowercase, non-alphanumeric → hyphen, collapse consecutive hyphens, trim.
 * @param {string} name
 * @returns {string}
 */
export function slugifyCheckName(name) {
  return String(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

/**
 * Build the canonical XLSX filename for a check.
 * Format: check-{NN}-{slug}.xlsx (2-digit zero-padded number).
 *
 * @param {number} checkNumber
 * @param {string} checkName
 * @returns {string}
 */
export function xlsxFilenameFor(checkNumber, checkName) {
  const padded = String(checkNumber).padStart(2, '0');
  const slug = slugifyCheckName(checkName);
  return `check-${padded}-${slug}.xlsx`;
}

/**
 * MEGAC-FIX F13: the ONE attachment-pointer sentence, everywhere a PDF surface
 * references an embedded per-check XLSX (top-issue cards, check blocks). The
 * old split ("Full list (N) attached…" vs "Complete list of all N activities
 * attached…") and its singular degrade ("all 1 item") are gone.
 *
 *   N > 1 → "Full list (N) attached as XLSX: <file> — open from the Attachments panel."
 *   N = 1 → "Attached as XLSX: <file> — open from the Attachments panel."
 *
 * @param {number} totalCount - true finding total (not the inline preview count)
 * @param {string} filename   - canonical xlsxFilenameFor() output
 * @returns {string}
 */
export function buildXlsxPointer(totalCount, filename) {
  const tail = `attached as XLSX: ${filename} — open from the Attachments panel.`;
  if (totalCount === 1) return `Attached as XLSX: ${filename} — open from the Attachments panel.`;
  return `Full list (${totalCount}) ${tail}`;
}
